/*
 * Copyright 2022 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import { AppLoadingIndicatorV1 } from "@startree-ui/platform-ui";
import React, { FunctionComponent, lazy, Suspense } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { useAuthProvider } from "../../components/auth-provider-loader/auth-provider-loader.component";
import { AuthenticationRequiredRoute } from "../../components/authentication-required-route/authentication-required-route.component";
import { AppRoute } from "../../utils/routes/routes.util";

const OrganizationsRouter = lazy(() =>
    import(
        /* webpackChunkName: "organizations-router" */
        "../organizations/organizations.router"
    ).then((module) => ({ default: module.OrganizationsRouter }))
);

const GeneralUnauthenticatedRouter = lazy(() =>
    import(
        /* webpackChunkName: "general-unauthenticated-router" */
        "../general-unauthenticated/general-unauthenticated.router"
    ).then((module) => ({ default: module.GeneralUnauthenticatedRouter }))
);

const HomePage = lazy(() =>
    import(
        /* webpackChunkName: "home-page" */
        "../../pages/home-page/home-page.component"
    ).then((module) => ({ default: module.HomePage }))
);

const NewUserPage = lazy(() =>
    import(
        /* webpackChunkName: "new-user-page" */
        "../../pages/new-user-page/new-user-page.component"
    ).then((module) => ({ default: module.NewUserPage }))
);

const LogoutPage = lazy(() =>
    import(
        /* webpackChunkName: "logout-page" */
        "../../pages/logout-page/logout-page.component"
    ).then((module) => ({ default: module.LogoutPage }))
);

const PageNotFoundPage = lazy(() =>
    import(
        /* webpackChunkName: "page-not-found-page" */
        "../../pages/page-not-found-page/page-not-found-page.component"
    ).then((module) => ({ default: module.PageNotFoundPage }))
);

export const AppRouter: FunctionComponent = () => {
    const { isAuthenticated } = useAuthProvider();

    return (
        <Suspense fallback={<AppLoadingIndicatorV1 />}>
            <Switch>
                {/* Base path (make sure this is first in the list) */}
                <Route
                    exact
                    path={AppRoute.BASE}
                    render={({ location }) => {
                        return isAuthenticated ? (
                            <Redirect
                                to={`${AppRoute.NEW_USER}${location.search}`}
                            />
                        ) : (
                            // If an error exists from Auth0, pass it over to signup
                            <Redirect
                                to={`${AppRoute.LOGIN}${location.search}`}
                            />
                        );
                    }}
                />
                {/* Direct all organizations paths to organizations router */}
                <AuthenticationRequiredRoute path={AppRoute.ORGANIZATIONS}>
                    <OrganizationsRouter />
                </AuthenticationRequiredRoute>

                {/* Home path */}
                <AuthenticationRequiredRoute exact path={AppRoute.HOME}>
                    <HomePage />
                </AuthenticationRequiredRoute>

                {/* New user path */}
                <AuthenticationRequiredRoute exact path={AppRoute.NEW_USER}>
                    <NewUserPage />
                </AuthenticationRequiredRoute>

                {/* Logout path */}
                <AuthenticationRequiredRoute exact path={AppRoute.LOGOUT}>
                    <LogoutPage />
                </AuthenticationRequiredRoute>

                {/* Direct all paths to general unauthenticated router */}
                <GeneralUnauthenticatedRouter />

                {/* No match found, render page not found */}
                <Route component={PageNotFoundPage} />
            </Switch>
        </Suspense>
    );
};
